import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { gamePackAPI, gameResultAPI } from '../api';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [gamePacks, setGamePacks] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGamePacks();
    loadGameResults();
  }, []);

  const loadGamePacks = async () => {
    try {
      const response = await gamePackAPI.getAll();
      setGamePacks(response.data);
    } catch (error) {
      console.error('Chyba při načítání herních balíčků:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameResults = async () => {
    try {
      const response = await gameResultAPI.getAll();
      setGameResults(response.data);
    } catch (error) {
      console.error('Chyba při načítání výsledků her:', error);
    }
  };

  const handleDeleteResult = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tento výsledek?')) return;
    try {
      await gameResultAPI.delete(id);
      setGameResults(gameResults.filter((r) => r._id !== id));
    } catch (error) {
      console.error('Chyba při mazání:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tento herní balíček?')) return;

    try {
      await gamePackAPI.delete(id);
      setGamePacks(gamePacks.filter((pack) => pack._id !== id));
    } catch (error) {
      console.error('Chyba při mazání:', error);
      alert('Chyba při mazání herního balíčku');
    }
  };

  const getGameModeLabel = (type) => {
    const labels = {
      'explain-it': 'Vysvětli to',
      'bug-hunt': 'Hledání chyb',
      'predict-output': 'Předpověz výstup',
      'code-golf': 'Code Golf',
      'verbal-to-code': 'Ze slov do kódu',
      'teacher-quiz': 'Kvíz',
      'fill-in-code': 'Doplň kód',
      'click-the-bug': 'Najdi chybu',
      'draw': 'Nakresli to',
      'teacher-draw': 'Nakresli to (učitel)',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Dashboard učitele
              </h1>
              <p className="text-gray-600 mt-1">Vítejte, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Odhlásit se
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <button
            onClick={() => navigate('/gamepack/new')}
            className="bg-green-500 text-white p-8 rounded-xl shadow-lg hover:bg-green-600 transition-all transform hover:scale-105"
          >
            <div className="text-4xl mb-2">➕</div>
            <div className="text-2xl font-bold">Vytvořit nový herní balíček</div>
          </button>

          <button
            onClick={() => navigate('/host')}
            className="bg-blue-500 text-white p-8 rounded-xl shadow-lg hover:bg-blue-600 transition-all transform hover:scale-105"
          >
            <div className="text-4xl mb-2">🎮</div>
            <div className="text-2xl font-bold">Hostovat hru</div>
          </button>
        </div>

        {/* Game Packs List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Moje herní balíčky ({gamePacks.length})
          </h2>

          {gamePacks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-xl">Zatím nemáte žádné herní balíčky</p>
              <p className="mt-2">Vytvořte svůj první balíček a začněte hrát!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gamePacks.map((pack) => (
                <div
                  key={pack._id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {pack.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {pack.description || 'Bez popisu'}
                  </p>
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Kola: {pack.rounds.length}
                    </div>
                    <div className="space-y-1">
                      {pack.rounds.slice(0, 3).map((round, idx) => (
                        <div
                          key={idx}
                          className="text-xs bg-gray-100 px-2 py-1 rounded"
                        >
                          {idx + 1}. {getGameModeLabel(round.type)}
                        </div>
                      ))}
                      {pack.rounds.length > 3 && (
                        <div className="text-xs text-gray-500">
                          ... a {pack.rounds.length - 3} dalších
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/gamepack/edit/${pack._id}`)}
                      className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      Upravit
                    </button>
                    <button
                      onClick={() => navigate(`/host?packId=${pack._id}`)}
                      className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
                    >
                      Spustit
                    </button>
                    <button
                      onClick={() => handleDelete(pack._id)}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {gameResults.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Odehrané hry ({gameResults.length})
            </h2>
            <div className="space-y-3">
              {gameResults.map((result) => (
                <div
                  key={result._id}
                  className={`flex items-center justify-between border rounded-lg p-4 ${
                    result.gradingComplete
                      ? 'border-green-200 bg-green-50'
                      : 'border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div>
                    <div className="font-bold text-gray-800">{result.gamePackName}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(result.finishedAt).toLocaleString('cs')}
                      {' | '}Místnost: {result.roomCode}
                      {' | '}{result.players?.length || 0} hráčů
                    </div>
                    <div className="text-xs mt-1">
                      {result.gradingComplete ? (
                        <span className="text-green-600 font-semibold">✅ Ohodnoceno</span>
                      ) : result.deferredGrading ? (
                        <span className="text-yellow-600 font-semibold">⏳ Čeká na hodnocení</span>
                      ) : (
                        <span className="text-gray-500">Dokončeno</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/review/${result._id}`)}
                      className={`px-4 py-2 rounded-lg text-white font-semibold text-sm ${
                        result.gradingComplete
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-yellow-500 hover:bg-yellow-600'
                      }`}
                    >
                      {result.gradingComplete ? 'Zobrazit' : 'Ohodnotit'}
                    </button>
                    <button
                      onClick={() => handleDeleteResult(result._id)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
